import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

const Section = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl md:text-3xl text-ink mb-4">
      <span className="text-gold tabular-nums mr-3">{n}.</span>{title}
    </h2>
    <div className="text-ink-muted leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-navy-900 text-ink">
      <header className="border-b border-gold/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Back to ScaleIQ
          </Link>
          <Logo size="sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="overline text-gold mb-3">Legal</div>
        <h1 className="font-display text-5xl md:text-6xl font-light mb-3">Privacy Policy</h1>
        <p className="text-sm text-ink-muted mb-12">Effective Date: June 30, 2026</p>

        <p className="text-ink-muted leading-relaxed mb-12">
          Welcome to ScaleIQ ("we," "our," or "us"). This Privacy Policy explains how we collect, use, and protect information when you visit and use our website and services.
        </p>

        <Section n="1" title="Information We Collect">
          <p>When you use our website, we may collect the following information:</p>
          <ul className="list-none space-y-2 pl-0">
            <li className="flex gap-3"><span className="text-gold">·</span>Name</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Email address</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Phone number</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Company name</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Business information you provide through questionnaires, forms, or assessments</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Payment information (processed securely through third-party payment providers)</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Website usage information such as IP address, browser type, pages visited, and device information</li>
          </ul>
        </Section>

        <Section n="2" title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-none space-y-2 pl-0">
            <li className="flex gap-3"><span className="text-gold">·</span>Provide business assessments, reports, and recommendations</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Process payments and deliver purchased services</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Communicate with you about your account, reports, or inquiries</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Improve our website, products, and services</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Send occasional marketing communications and updates (you may unsubscribe at any time)</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Analyze website traffic and user behavior</li>
          </ul>
        </Section>

        <Section n="3" title="Payment Processing">
          <p>Payments made through our website are processed by trusted third-party payment providers. We do not store your full credit card information on our servers.</p>
        </Section>

        <Section n="4" title="Cookies and Analytics">
          <p>Our website may use cookies and analytics tools to improve user experience and understand how visitors use the site. These technologies help us analyze traffic and improve our services.</p>
          <p>You may disable cookies through your browser settings; however, some features of the website may not function properly.</p>
        </Section>

        <Section n="5" title="Sharing Information">
          <p>We do not sell, rent, or trade your personal information to third parties.</p>
          <p>We may share information with trusted service providers who help us operate our business, including:</p>
          <ul className="list-none space-y-2 pl-0">
            <li className="flex gap-3"><span className="text-gold">·</span>Payment processors</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Email service providers</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Website hosting providers</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Analytics providers</li>
          </ul>
          <p>These providers may access information only as necessary to perform services on our behalf.</p>
          <p>We may also disclose information if required by law or to protect our legal rights.</p>
        </Section>

        <Section n="6" title="Data Security">
          <p>We take reasonable measures to protect your information from unauthorized access, disclosure, or misuse. However, no internet transmission or electronic storage system is completely secure, and we cannot guarantee absolute security.</p>
        </Section>

        <Section n="7" title="Your Choices">
          <p>You may:</p>
          <ul className="list-none space-y-2 pl-0">
            <li className="flex gap-3"><span className="text-gold">·</span>Request access to the personal information we hold about you</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Request correction or deletion of your information</li>
            <li className="flex gap-3"><span className="text-gold">·</span>Opt out of marketing communications at any time by following unsubscribe instructions in our emails</li>
          </ul>
          <p>To make a request, please contact us using the information below.</p>
        </Section>

        <Section n="8" title="Third-Party Links">
          <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites.</p>
        </Section>

        <Section n="9" title="Children's Privacy">
          <p>Our services are intended for business owners and individuals over the age of 18. We do not knowingly collect personal information from children under 13.</p>
        </Section>

        <Section n="10" title="Changes to This Privacy Policy">
          <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.</p>
        </Section>

        <Section n="11" title="Contact Us">
          <p>If you have questions about this Privacy Policy or our privacy practices, please contact us at:</p>
          <div className="border border-gold/20 p-5 mt-3">
            <div className="font-display text-xl text-ink mb-1">ScaleIQ</div>
            <div>Email: <a href="mailto:kevin@tryscaleiq.com" className="text-gold hover:underline">kevin@tryscaleiq.com</a></div>
            <div>Website: <a href="https://tryscaleiq.com" className="text-gold hover:underline">https://tryscaleiq.com</a></div>
          </div>
        </Section>

        <div className="mt-16 pt-8 border-t border-gold/10 text-xs text-ink-muted flex justify-between">
          <Link to="/terms" className="hover:text-ink">Terms of Service →</Link>
          <Link to="/" className="hover:text-ink">Back to ScaleIQ →</Link>
        </div>
      </main>
    </div>
  );
}
